import { useId } from '@reach/auto-id'
import i18next from 'i18next'
import { get } from 'lodash'
import ReactDatePicker from 'react-date-picker/dist/entry.nostyle'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

import Icon from './Icon'

const PickerWrap = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledReactDatePicker = styled(ReactDatePicker)`
  ${({ theme }: any) => themeUtilities.getFontDefinition(theme)};
  background-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'background')};
  width: 100%;

  /* Calendar */

  /* TODO refine this more to make it agree with the rules, but this is ripped nearly 1:1 from the library we use */
  .react-calendar {
    width: 290px;
    ${({ theme }: any) => themeUtilities.getFontDefinition(theme)};
    border: 1px solid
      ${({ theme, rootContainer, displayAsType }: any) =>
        themeUtilities.getColor(
          theme,
          rootContainer,
          get(theme, `inputs.types.${displayAsType}.borderColor`, 'light')
        )};
    background-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'background')};

    &__navigation {
      height: 44px;
      margin-bottom: 1em;

      button {
        min-width: 44px;
        background: none;

        &:enabled {
          &:hover,
          &:focus {
            background-color: rgb(230 230 230);
          }
        }

        &[disabled] {
          background-color: rgb(240 240 240);
        }
      }
    }

    &__tile {
      max-width: 100%;
      text-align: center;
      padding: 0.75em 0.5em;
      background: none;

      &--now {
        color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'primary')};
        border: 1px solid !important;
        border-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'default')};
        border-radius: 50%;

        &:hover {
          border-radius: inherit;
        }
      }

      &:disabled {
        background-color: ${({ theme, rootContainer }: any) =>
          themeUtilities.getColor(theme, rootContainer, 'container', 'root')};
        color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'secondary')};
      }

      &:enabled {
        &:hover,
        &:focus {
          background-color: ${({ theme, rootContainer }: any) =>
            themeUtilities.getColor(theme, rootContainer, 'light')};
          color: ${({ theme, rootContainer }: any) =>
            themeUtilities.getColor(theme, rootContainer, 'light', 'container')};
        }
      }

      &--active {
        background-color: ${({ theme, rootContainer }: any) =>
          themeUtilities.getColor(theme, rootContainer, 'primary')};
        color: ${({ theme, rootContainer }: any) =>
          themeUtilities.getColor(theme, rootContainer, 'primary', 'container')};
      }
    }

    &__month-view {
      &__weekdays {
        text-align: center;
        text-transform: uppercase;
        font-weight: bold;
        font-size: 0.75em;

        &__weekday {
          padding: 0.5em;
          color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};
        }
      }

      &__weekNumbers {
        font-weight: bold;

        .react-calendar__tile {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75em;
          padding: calc(0.75em / 0.75) calc(0.5em / 0.75);
        }
      }

      &__days {
        &__day {
          &--weekend {
            color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'attention')};
          }

          &--neighboringMonth {
            color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'light')};
          }
        }
      }
    }

    &__year-view,
    &__decade-view,
    &__century-view {
      .react-calendar__tile {
        padding: 2em 0.5em;
      }
    }

    &--selectRange {
      .react-calendar__tile {
        &--hover {
          background-color: ${({ theme, rootContainer }: any) =>
            themeUtilities.getColor(theme, rootContainer, 'light')};
          color: ${({ theme, rootContainer }: any) =>
            themeUtilities.getColor(theme, rootContainer, 'light', 'container')};
        }
      }
    }

    button {
      margin: 0;
      border: 0;
      outline: none;

      &:enabled {
        &:hover {
          cursor: pointer;
        }
      }
    }
  }

  /* Date Picker */

  &.react-date-picker--disabled .react-date-picker__wrapper {
    background-color: ${({ theme, rootContainer }: any) =>
      themeUtilities.getColor(theme, rootContainer, 'secondary', 'root')};
  }

  .react-date-picker {
    display: inline-flex;
    position: relative;

    &__wrapper {
      display: flex;
      padding: 5px;
      width: 100%;
      border: 1px solid ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'light')};
      border-radius: 4px;
      height: 38px;
      background-color: ${({ theme, rootContainer }: any) =>
        themeUtilities.getColor(theme, rootContainer, 'background')};
    }

    &__inputGroup {
      min-width: calc((4px * 3) + 0.54em * 8 + 0.217em * 2);
      flex-grow: 1;
      display: flex;
      padding: 0 2px;
      align-items: baseline;
      box-sizing: content-box;

      &__divider {
        padding: 1px 0;
        white-space: pre;
        color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};
      }

      &__input {
        min-width: 0.54em;
        height: 100%;
        position: relative;
        padding: 0 1px;
        border: 0;
        background: none;
        font: inherit;
        box-sizing: content-box;
        color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          appearance: none;
          margin: 0;
        }

        &--hasLeadingZero {
          margin-left: -0.54em;
          padding-left: calc(1px + 0.54em);
        }
      }
    }

    &__button {
      border: 0;
      background: transparent;
      padding: 4px 6px;

      &:enabled {
        cursor: pointer;

        &:hover,
        &:focus {
          .react-date-picker__button__icon {
            stroke: rgb(0 120 215);
          }
        }
      }

      svg {
        display: inherit;
      }
    }

    &__calendar {
      width: 350px;
      max-width: 100vw;
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 1;

      &--closed {
        display: none;
      }

      .react-calendar {
        border-width: thin;
      }
    }
  }
`
const StyledIcon = styled(Icon)`
  stroke: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'default')};
  fill: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'default')};
`

// TODO Update the icons to use `currentColor` and have them decide how to use it instead of having to know
// the inner details of how an icon is rendered
const StyledCalendarIcon = styled(Icon)`
  g {
    fill: ${({ theme, rootContainer, disabled }: any) =>
      themeUtilities.getColor(theme, disabled ? 'secondary' : rootContainer, 'primary')};
  }

  margin-top: -2px;
  width: 20px;
  height: 20px;
`

const Label = styled.label`
  ${({ theme }: any) => themeUtilities.getFontDefinition(theme, { weight: 'bolder' })};
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};
`

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const RequiredIndicator = styled.span`
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'attention')};
  padding: 0 5px;
`

const Error = styled.p`
  margin: 5px 0 0;
  background-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'background')};
  ${({ theme }: any) => themeUtilities.getFontDefinition(theme)};
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'attention')};
`

interface DatePickerProps {
  className?: string
  disabled?: boolean
  error?: string
  id?: string
  innerRef?: object
  isClearable?: boolean
  isRequired?: boolean
  label?: string
  maxDate?: object
  minDate?: object
  name?: string
  onCalendarClose?(...args: unknown[]): unknown
  onChange?(...args: unknown[]): unknown
  rootContainer?: string
  value?: any
}

// TODO Wrap with the standard `Input` component to unify the look/feel of all the inputs
const DatePicker = ({
  rootContainer,
  onCalendarClose,
  onChange,
  value,
  disabled,
  isClearable,
  label,
  innerRef,
  isRequired,
  error,
  name,
  className,
  minDate,
  maxDate
}: DatePickerProps) => {
  const { t } = useTranslation('shared')
  const id = useId()

  return (
    // @ts-expect-error
    <PickerWrap className={className} rootContainer={rootContainer} displayAsType={'default'}>
      {isRequired && (
        // @ts-expect-error
        <RequiredIndicator rootContainer={rootContainer} aria-label={t('shared:RequiredIndicator.label')}>
          {t('shared:RequiredIndicator.icon')}
        </RequiredIndicator>
      )}
      <InputWrapper>
        {/* @ts-expect-error */}
        {label && <Label htmlFor={id}>{label}</Label>}
        <StyledReactDatePicker
          id={id}
          rootContainer={rootContainer}
          displayAsType={disabled ? 'disabled' : 'default'}
          onChange={onChange}
          value={value}
          disabled={disabled}
          isClearable={isClearable}
          highlightDates={[new Date()]}
          todayButton={t('shared:DatePicker.todayLabel')}
          locale={i18next.languages[0]}
          ref={innerRef}
          name={name}
          minDate={minDate}
          maxDate={maxDate}
          // @ts-expect-error
          onCalendarClose={() => onCalendarClose(value)}
          amPmAriaLabel={t('DatePicker.amPmAriaLabel')}
          calendarAriaLabel={t('DatePicker.calendarAriaLabel')}
          clearAriaLabel={isClearable ? t('DatePicker.clearAriaLabel') : false}
          dayAriaLabel={t('DatePicker.dayAriaLabel')}
          dayPlaceholder={t('DatePicker.dayPlaceholder')}
          monthAriaLabel={t('DatePicker.monthAriaLabel')}
          monthPlaceholder={t('DatePicker.monthPlaceholder')}
          nativeInputAriaLabel={t('DatePicker.nativeInputAriaLabel')}
          yearAriaLabel={t('DatePicker.yearAriaLabel')}
          yearPlaceholder={t('DatePicker.yearPlaceholder')}
          // @ts-expect-error
          calendarIcon={<StyledCalendarIcon name={'calendar'} disabled={disabled} />}
          // @ts-expect-error
          clearIcon={isClearable ? <StyledIcon type="fill" name={'clear'} /> : false}
        />
        {/* @ts-expect-error */}
        {error && <Error rootContainer={rootContainer}>{error}</Error>}
      </InputWrapper>
    </PickerWrap>
  )
}

DatePicker.displayName = 'DatePicker'

DatePicker.defaultProps = {
  rootContainer: 'default',
  isClearable: true,
  onChange: () => {},
  onCalendarClose: () => {}
}

export default DatePicker
